import {useContext, useEffect, useState} from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  HStack,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import {FiClock, FiRefreshCw} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "../Contexts";
import {DeviceTelemetry} from "../components/DeviceTelemetry";

const animationModes = [
  {value: 0, label: "Fixed"},
  {value: 1, label: "Move left"},
  {value: 2, label: "Move right"},
  {value: 3, label: "Blink"},
];

const screenTimeouts = [
  {value: 0, label: "Always on"},
  {value: 1, label: "30 sec"},
  {value: 2, label: "1 min"},
  {value: 3, label: "3 min"},
  {value: 4, label: "5 min"},
];

const displayTriggers = [
  {value: 0, label: "Touch"},
  {value: 1, label: "Fixed"},
];

function SegmentedSetting({label, value, options, onChange, isDisabled}) {
  return (
    <Stack spacing={3}>
      <Flex align="center" justify="space-between" gap={3}>
        <Text fontWeight="semibold">{label}</Text>
        <Badge variant="subtle" colorScheme="gray">
          {options.find((option) => option.value === value)?.label ?? "Unavailable"}
        </Badge>
      </Flex>
      <Flex
        width="fit-content"
        maxW="100%"
        wrap="wrap"
        gap="3px"
        p="3px"
        bg="#111315"
        border="1px solid"
        borderColor="#2b2f34"
        borderRadius="5px"
      >
        {options.map((option) => (
          <Button
            key={option.value}
            size="sm"
            variant={value === option.value ? "solid" : "ghost"}
            colorScheme={value === option.value ? "studio" : "gray"}
            color={value === option.value ? "gray.900" : "gray.300"}
            borderRadius="4px"
            onClick={() => onChange(option.value)}
            isDisabled={isDisabled}
          >
            {option.label}
          </Button>
        ))}
      </Flex>
    </Stack>
  );
}

export function Effects() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const [speed, setSpeed] = useState(20);
  const [animationMode, setAnimationMode] = useState(0);
  const [screenTimeout, setScreenTimeout] = useState(0);
  const [displayTrigger, setDisplayTrigger] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    if (!connected) return;
    const effects = adapter.getEffects();
    setAnimationMode(effects.animationMode);
    setSpeed(effects.speed);
    setScreenTimeout(effects.screenTimeout);
    setDisplayTrigger(effects.displayTrigger);
  }, [connected, adapter]);

  const apply = async (action, nextValue, setter) => {
    setter(nextValue);
    try {
      await action(nextValue);
    } catch (error) {
      toast({title: "Setting failed", description: error.message, status: "error", duration: 4000});
    }
  };

  return (
    <Stack spacing={8}>
      <DeviceTelemetry />
      <Divider borderColor="#292d31" />
      <Grid templateColumns={{base: "1fr", lg: "minmax(0, 1.2fr) minmax(280px, .8fr)"}} gap={{base: 8, lg: 12}}>
        <Stack spacing={7}>
          <Text color="#6e747c" fontSize="10px" textTransform="uppercase" fontWeight="800">Display behavior</Text>
          <SegmentedSetting
            label="Dynamic effect"
            value={animationMode}
            options={animationModes}
            isDisabled={!connected}
            onChange={(value) => apply(adapter.setAnimationMode.bind(adapter), value, setAnimationMode)}
          />

          <Divider borderColor="#292d31" />

          <Stack spacing={3}>
            <Flex align="center" justify="space-between">
              <Text fontWeight="semibold">Effect speed</Text>
              <Badge colorScheme="gray">{speed}</Badge>
            </Flex>
            <HStack spacing={4}>
              <Text fontSize="xs" color="gray.500">Slow</Text>
              <Slider
                min={0}
                max={100}
                value={speed}
                onChange={setSpeed}
                onChangeEnd={(value) => apply(adapter.setAnimationSpeed.bind(adapter), value, setSpeed)}
                isDisabled={!connected}
                focusThumbOnChange={false}
              >
              <SliderTrack bg="#272b2f"><SliderFilledTrack bg="#ffd84d" /></SliderTrack>
                <SliderThumb boxSize={4} />
              </Slider>
              <Text fontSize="xs" color="gray.500">Fast</Text>
            </HStack>
          </Stack>

          <Divider borderColor="#292d31" />

          <SegmentedSetting
            label="Screen timeout"
            value={screenTimeout}
            options={screenTimeouts}
            isDisabled={!connected}
            onChange={(value) => apply(adapter.setScreenTimeout.bind(adapter), value, setScreenTimeout)}
          />
        </Stack>

        <Stack spacing={7} borderTop={{base: "1px solid", lg: 0}} borderLeft={{base: 0, lg: "1px solid"}} borderColor="#292d31" pt={{base: 7, lg: 0}} pl={{base: 0, lg: 8}}>
          <Text color="#6e747c" fontSize="10px" textTransform="uppercase" fontWeight="800">Device controls</Text>
          <SegmentedSetting
            label="Display trigger"
            value={displayTrigger}
            options={displayTriggers}
            isDisabled={!connected || displayTrigger === null}
            onChange={(value) => apply(adapter.setDisplayTrigger.bind(adapter), value, setDisplayTrigger)}
          />

          <Divider borderColor="#292d31" />

          <Box>
            <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold" mb={4}>
              Device clock
            </Text>
            <Button
              leftIcon={syncing ? <FiRefreshCw /> : <FiClock />}
              variant="outline"
              borderColor="#34383e"
              borderRadius="5px"
              isDisabled={!connected}
              isLoading={syncing}
              onClick={async () => {
                setSyncing(true);
                try {
                  await adapter.syncTime();
                  toast({title: "Device time synchronized", status: "success", duration: 1800});
                } catch (error) {
                  toast({title: "Time sync failed", description: error.message, status: "error", duration: 4000});
                } finally {
                  setSyncing(false);
                }
              }}
            >
              Sync current time
            </Button>
          </Box>
        </Stack>
      </Grid>
    </Stack>
  );
}
